import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Snackbar,
  IconButton,
  InputAdornment,
  LinearProgress,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../GASetup/AnalyticsTracker";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import {
  cardHover,
  cardOutline,
  headingFonts,
  mainButtonStyles,
  textFieldStyles,
} from "../../Styles/Styles";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [strength, setStrength] = useState(0);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (strength < 3) {
      setError(
        "Password must include letters, numbers, and special characters."
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token, password }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        setMessage("Password successfully reset. Redirecting...");
        setTimeout(() => navigate("/get-started"), 3000);
      } else {
        setError(data.message || "Failed to reset password.");
      }
    } catch (err) {
      setError(
        "Error resetting password. Please check your internet connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const calculateStrength = (password) => {
    let strengthScore = 0;
    const criteria = [
      /[a-zA-Z]/.test(password), // contains letters
      /\d/.test(password), // contains numbers
      /[!@#$%^&*(),.?":{}|<>]/.test(password), // contains special characters
      password.length >= 8, // length at least 8
    ];

    criteria.forEach((criterion) => {
      if (criterion) strengthScore++;
    });

    setStrength(strengthScore);
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ marginTop: 8 }}>
      <Helmet>
        <title>Reset Password - Listed.ky</title>
        <meta
          name="description"
          content="Reset your password on Listed.ky securely."
        />
      </Helmet>
      <AnalyticsTracker />
      <Box
        sx={{
          bgcolor: "background.paper",
          padding: 3,
          ...cardOutline,
          ...cardHover,
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{ ...headingFonts }}
        >
          Reset Password
        </Typography>
        <form onSubmit={handleResetPassword}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              calculateStrength(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ ...textFieldStyles }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ ...textFieldStyles }}
          />

          {/* Password Strength Meter */}
          <Box sx={{ marginTop: 2 }}>
            <LinearProgress
              variant="determinate"
              value={(strength / 4) * 100}
            />
            <Typography variant="caption" align="right">
              {strength === 0
                ? "Enter a password"
                : strength === 1
                ? "Weak"
                : strength === 2
                ? "Moderate"
                : "Strong"}
            </Typography>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            className="main-button-style"
            disabled={loading}
            sx={{
              mt: 2,
              ...mainButtonStyles,
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              <>
                <span>Reset Password</span>
                <ArrowForwardOutlinedIcon />
              </>
            )}
          </Button>
        </form>

        {/* Snackbar Notifications */}
        <Snackbar
          open={!!message}
          autoHideDuration={6000}
          onClose={() => setMessage("")}
        >
          <Alert severity="success">{message}</Alert>
        </Snackbar>
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError("")}
        >
          <Alert severity="error">{error}</Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
